import { VehicleType } from '@repo/shared'
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TableCell,
  TableRow,
} from '@repo/ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { Edit, Eye, Link2, MoreVertical, Trash, Truck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDeleteVehicle } from '../../hooks/use-delete-vehicle'
import AssignTrailerDialog from './assign-trailer-dialog'

import type { VehicleWithTruck } from '@repo/shared'

import { ConfirmDeleteDialog } from '@/components/shared/confirm-delete-dialog'
import { VehicleStatusBadge } from '@/components/shared/vehicle-status-badge'
import { getManufacturerIcon } from '@/hooks/use-trucks-manufacturers'

type Props = {
  vehicle: VehicleWithTruck
}

export function TruckTableRow({ vehicle }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const ManufacturerIcon = getManufacturerIcon(vehicle.manufacturer)

  const { mutate: deleteVehicle } = useDeleteVehicle()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isTrailerDialogOpen, setIsTrailerDialogOpen] = useState(false)

  const handleViewDetails = () => {
    navigate({
      to: '/dashboard/vehicles/$vehicleId',
      params: { vehicleId: String(vehicle.id) },
      search: { type: VehicleType.Truck },
    })
  }

  const handleDelete = () => {
    deleteVehicle(Number(vehicle.id))
    setIsDeleteOpen(false)
  }

  return (
    <>
      <TableRow
        className="bg-background hover:bg-muted/50 cursor-pointer"
        onClick={handleViewDetails}
      >
        <TableCell>
          <div className="flex items-center gap-3">
            <div className="bg-background flex size-9 items-center justify-center rounded-xs border">
              {ManufacturerIcon ? (
                <ManufacturerIcon className="size-6" />
              ) : (
                <Truck className="text-primary size-4" />
              )}
            </div>
            <div>
              <p className="font-medium">{vehicle.licensePlate}</p>
              <p className="text-muted-foreground text-xs">
                {vehicle.vinNumber}
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell>{vehicle.manufacturer}</TableCell>
        <TableCell>{vehicle.truck?.vehicleModel}</TableCell>
        <TableCell>
          <VehicleStatusBadge status={vehicle.status} />
        </TableCell>
        <TableCell>
          {vehicle.assignedTrailer ? (
            <Badge
              variant="secondary"
              className="hover:bg-secondary/80 cursor-pointer transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                setIsTrailerDialogOpen(true)
              }}
            >
              <Link2 className="mr-1 h-3 w-3" />
              {vehicle.assignedTrailer.licensePlate}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </TableCell>
        <TableCell>
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon-sm"
                  className="bg-background"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleViewDetails}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('common:viewDetails')}
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/dashboard/vehicles/$vehicleId/edit"
                    params={{ vehicleId: vehicle.id.toString() }}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    {t('common:edit')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsTrailerDialogOpen(true)}>
                  <Link2 className="mr-2 h-4 w-4" />
                  {t('vehicles:changeTrailer')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setIsDeleteOpen(true)}
                >
                  <Trash className="mr-2 h-4 w-4" />
                  {t('common:delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>

      <ConfirmDeleteDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDelete}
        title={t('vehicles:deleteTruck.title')}
        description={t('vehicles:deleteTruck.description')}
        cancelLabel={t('vehicles:deleteTruck.cancel')}
        confirmLabel={t('vehicles:deleteTruck.confirm')}
      />

      <AssignTrailerDialog
        truckId={vehicle.id}
        currentTrailerId={vehicle.assignedTrailer?.id}
        open={isTrailerDialogOpen}
        onOpenChange={setIsTrailerDialogOpen}
      />


    </>
  )
}
